import { attributes, html } from 'BomdisoftComponents/html/index';

export type NavDockItemOptions = {
  annotation?: string;
  annotationAction?: string;
  label: string;
  testId: string;
  /** Route-nav mode: link target. Omit for view-switch mode. */
  href?: string;
  /** Route-nav mode: paths that mark this item active. */
  matchPaths?: string[];
  /** Route-nav mode: require an exact path match. */
  exact?: boolean;
  /** View-switch mode: explicit active state (overrides path matching). */
  active?: boolean;
};

/**
 * Per-app visual overrides for the dock. Every field is optional and falls back to the design
 * tokens, so apps that already brand their tokens need not pass anything. Apps whose page palette
 * diverges from their theme primary (warm/dark landings) pass these to make the dock match their
 * look instead of the generic primary.
 */
export type NavDockAppearance = {
  /** Active item text + default focus-ring colour. Falls back to `--bomdisoft-primary`. */
  accent?: string;
  /** Background of the active item pill. Falls back to a 15% tint of `--bomdisoft-primary`. */
  activeBackground?: string;
  /** Dock border colour. Falls back to `--bomdisoft-divider`. */
  borderColor?: string;
  /** Idle item text colour. Falls back to `--bomdisoft-text-secondary`. */
  color?: string;
  /** Focus-ring colour. Falls back to `accent`, then `--bomdisoft-primary`. */
  focusColor?: string;
  /** Item text colour on hover. Falls back to `--bomdisoft-text-primary`. */
  hoverColor?: string;
  /** Box-shadow of the dock. */
  shadow?: string;
  /** Dock surface (the frosted pill) background. Falls back to translucent `--bomdisoft-background-paper`. */
  surface?: string;
};

export type NavDockOptions = {
  appearance?: NavDockAppearance;
  items: readonly NavDockItemOptions[];
  lang?: string;
  pathname: string;
  testId?: string;
};

export const toNavDockVariables = (appearance: NavDockAppearance | undefined) => {
  const overrides: Record<string, string> = {};
  if (appearance?.accent) overrides['--dock-accent'] = appearance.accent;
  if (appearance?.activeBackground) overrides['--dock-active-background'] = appearance.activeBackground;
  if (appearance?.borderColor) overrides['--dock-border'] = appearance.borderColor;
  if (appearance?.color) overrides['--dock-color'] = appearance.color;
  if (appearance?.focusColor) overrides['--dock-focus'] = appearance.focusColor;
  if (appearance?.hoverColor) overrides['--dock-hover-color'] = appearance.hoverColor;
  if (appearance?.shadow) overrides['--dock-shadow'] = appearance.shadow;
  if (appearance?.surface) overrides['--dock-surface'] = appearance.surface;
  return overrides;
};

export const isNavDockItemActive = (item: NavDockItemOptions, normalizedPath: string) => {
  if (typeof item.active === 'boolean') return item.active;
  return (item.matchPaths ?? []).some(raw => {
    const p = raw.replace(/\/+$/, '');
    if (item.exact) return normalizedPath === p;
    return normalizedPath === p || normalizedPath.startsWith(`${p}/`);
  });
};

export const toRenderedPath = (pathname: string, lang: string | undefined) => {
  const path = pathname.replace(/\/+$/, '');
  if (!lang || path === `/${lang}` || path.startsWith(`/${lang}/`)) return path;
  return `/${lang}${path}`;
};

export const renderNavDock = ({ appearance, items, lang, pathname, testId = 'nav-dock' }: NavDockOptions) => {
  const normalizedPath = toRenderedPath(pathname, lang);
  const variables = Object.entries(toNavDockVariables(appearance)).map(([name, value]) => `${name}: ${value}`);
  const rows = items.map(item => {
    const shared = { class: 'bomdisoft-nav-dock-item', 'data-active': isNavDockItemActive(item, normalizedPath) ? 'true' : undefined, 'data-prodantix': item.annotation, 'data-prodantix-action': item.annotationAction, 'data-testid': item.testId };
    return item.href ? html`<a${attributes({ ...shared, href: item.href })}>${item.label}</a>` : html`<button${attributes({ ...shared, type: 'button' })}>${item.label}</button>`;
  });
  return html`<nav${attributes({ class: 'bomdisoft-nav-dock', 'data-testid': testId, style: variables.length > 0 ? variables.join('; ') : undefined })}>${rows}</nav>`;
};
